// Lib dependencies
import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Trans, useTranslation } from 'react-i18next';

// UI lib dependencies
import {
  Alert,
  Anchor,
  Button,
  Container,
  Group,
  Image,
  Stack,
  Text,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconMailExclamation } from '@tabler/icons-react';

// Services
import {
  AuthIdentityErrorCode,
  getInvitedEmail,
  requestInvitationResend,
} from '@services/invitation';
import { setViewAsUser } from '@app/shared/providers/ViewAsProvider/store';

type Props = {
  code: AuthIdentityErrorCode;
  onRetry: () => void;
};

// Codes the invitee can solve from their mailbox (verification or new link).
// The others need another Auth0 account or sign-in method.
const MAILBOX_RECOVERABLE: AuthIdentityErrorCode[] = [
  'EMAIL_NOT_VERIFIED',
  'INVITATION_INVALID',
];

/**
 * Explains why the signed-in Auth0 identity cannot open its account yet, and
 * offers the next step (verify the email, get a new link, switch account).
 */
function AuthIdentityNotice({ code, onRetry }: Props) {
  const { getAccessTokenSilently, logout, loginWithRedirect } = useAuth0();
  const { t } = useTranslation();
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>(
    'idle',
  );

  const invitedEmail = getInvitedEmail();
  const key = `GateLayout:AuthIdentityNotice.${code}`;
  const mailboxRecoverable = MAILBOX_RECOVERABLE.includes(code);
  const renewal = code === 'SESSION_RENEWAL_REQUIRED';

  async function onResend() {
    setResendState('sending');
    try {
      await requestInvitationResend(await getAccessTokenSilently());
      setResendState('sent');
      showNotification({
        title: t('GateLayout:AuthIdentityNotice.resend.successTitle'),
        message: t('GateLayout:AuthIdentityNotice.resend.successMessage'),
        color: 'blackroomGreen',
      });
    } catch {
      setResendState('idle');
      showNotification({
        title: t('GateLayout:AuthIdentityNotice.resend.errorTitle'),
        message: t('GateLayout:AuthIdentityNotice.resend.errorMessage'),
        color: 'red',
      });
    }
  }

  function onLogout() {
    // The invitation stays stored for this tab, so the next sign-in is
    // prefilled with the invited email and still carries the token.
    setViewAsUser(null);
    logout({ logoutParams: { returnTo: window.location.origin } });
  }

  return (
    <Container h="100vh" w="100%" fluid>
      <Stack gap="xl" h="100%" align="center" justify="center">
        <Image alt="Blackroom" src="/images/brand/logo.svg" w={70} h={70} />
        <Alert
          icon={<IconMailExclamation />}
          title={t(`${key}.title`)}
          color="blackroomOrange"
          maw={520}
          data-testid={`auth-identity-notice-${code}`}
        >
          <Stack gap="md">
            <Text c="blackroomWhite">
              <Trans
                i18nKey={
                  code === 'INVITATION_EMAIL_MISMATCH' && !invitedEmail
                    ? `${key}.messageWithoutEmail`
                    : `${key}.message`
                }
                values={{ email: invitedEmail ?? '' }}
                components={{ b: <b /> }}
              />
            </Text>
            {renewal || mailboxRecoverable ? (
              <Group gap="sm">
                {renewal ? (
                  <Button radius="xs" onClick={() => loginWithRedirect()}>
                    {t(`${key}.signIn`)}
                  </Button>
                ) : null}
                {mailboxRecoverable ? (
                  <Button radius="xs" onClick={onRetry}>
                    {t(`${key}.retry`)}
                  </Button>
                ) : null}
                {mailboxRecoverable ? (
                  <Button
                    radius="xs"
                    variant="light"
                    loading={resendState === 'sending'}
                    disabled={resendState === 'sent'}
                    onClick={onResend}
                  >
                    {t(
                      resendState === 'sent'
                        ? 'GateLayout:AuthIdentityNotice.resend.sent'
                        : 'GateLayout:AuthIdentityNotice.resend.cta',
                    )}
                  </Button>
                ) : null}
              </Group>
            ) : null}
            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="left"
              onClick={onLogout}
            >
              {t(
                mailboxRecoverable || renewal
                  ? 'GateLayout:AuthIdentityNotice.logout'
                  : 'GateLayout:AuthIdentityNotice.switchAccount',
              )}
            </Anchor>
          </Stack>
        </Alert>
      </Stack>
    </Container>
  );
}

export default AuthIdentityNotice;
